// @flow
import { createSelector } from 'reselect';
import { productAvailabilityEnum } from '../utils/segment';
import { getDeviceType } from '../utils/device';
import { sortIdToTrackingValue } from '../utils/sort';
import { selectPathToSelectedCategory } from './categoriesSelectors';
import {
  selectProduct,
  selectVariant,
  selectIsProductInStock,
  selectProductIds,
} from './productsSelectors';
import type { TrackingData, BoostTrackingData, State } from '../sharedTypes';

function getPositionLocation(path, trackDisplayResults) {
  if (trackDisplayResults) return 'Search Result';
  const isAllCategorySelected =
    path && path[0] && path[0].toLowerCase() === 'all';
  return isAllCategorySelected ? 'Main Page' : path.join(', ');
}

function getProductPosition(productIds, productId) {
  const displayOrderIds = [
    ...productIds.featuredProducts,
    ...productIds.inStock,
    ...productIds.sponsored,
    ...productIds.oos,
  ];
  return displayOrderIds.indexOf(productId);
}

export const selectTrackingData: (
  State,
  // TODO: remove productId from required properties,
  // since we can deduce the productId from the variantId
  {
    productId: string,
    variantId: string,
    selectedCategoryId: string | null,
  },
  trackDisplayResults: boolean
) => TrackingData = createSelector(
  (state) => state.userState,
  (state) => state.campaignState.campaignId,
  (state) => state.campaignState.campaignName,
  (state) => state.searchState.searchResults,
  (state) => state.socialEcommState.team.teamId,
  (state, props, trackDisplayResults) => trackDisplayResults,
  (state) => state.sortState.selectedSort,
  (state) => state.optimizelyState,
  selectPathToSelectedCategory,
  selectProduct,
  selectVariant,
  selectIsProductInStock,
  selectProductIds,
  (
    userState,
    campaignId,
    campaignName,
    searchResults,
    teamId,
    trackDisplayResults,
    selectedSort,
    optimizelyState,
    pathToSelectedCategory,
    product,
    variant,
    isProductInStock,
    productIds
  ) => {
    const productLegacyPostId = product.legacyPostId;
    const variantLegacyPostId = variant.legacyPostId;
    const { userId, role, subscriptionState, planCode } = userState;
    const productDisplayOrderPosition = getProductPosition(
      productIds,
      variant.parentId
    );
    const displayResults = trackDisplayResults
      ? {
          display_results: productIds.inStock.length,
          display_results_oos: productIds.oos.length,
        }
      : {};
    return {
      user_id: userId || null,
      subscription_state: subscriptionState && subscriptionState.toLowerCase(),
      role,
      plan_code: planCode,
      campaign_id: campaignId,
      campaign_name: campaignName,
      team_id: teamId,
      product_id: variantLegacyPostId,
      parent_product_id:
        variantLegacyPostId !== productLegacyPostId ? productLegacyPostId : 0,
      product_name: product.name,
      product_categories: product.categoryNames,
      merch_position_order: product.ordering,
      event_position_order: productDisplayOrderPosition + 1, // should start at 1
      retail_price: variant.price,
      sale_price: variant.salePrice,
      product_image_url: product.imageUrl,
      product_availability: isProductInStock
        ? productAvailabilityEnum.inStock
        : productAvailabilityEnum.outOfStock,
      position_location: getPositionLocation(
        pathToSelectedCategory,
        searchResults && trackDisplayResults
      ),
      ...displayResults,
      // Technically, this makes this selector impure. Generally we should avoid
      // making selectors impure, since selectors use memoization. Here it's ok
      // because a user can't switch device type in the middle of a session.
      device: getDeviceType(),
      sort_name: sortIdToTrackingValue[selectedSort],
    };
  }
);

export const selectPageTrackingFullData: (State) => BoostTrackingData =
  createSelector(
    (state) => state.userState,
    (state) => state.campaignState.campaignId,
    (state) => state.campaignState.campaignName,
    (state) => state.sortState.selectedSort,
    (state) => state.optimizelyState,
    (state) => state.socialEcommState.team.teamId,
    (
      userState,
      campaignId,
      campaignName,
      selectedSort,
      optimizelyState,
      teamId
    ) => {
      const { userId, role, subscriptionState, planCode } = userState;
      return {
        user_id: userId || null,
        subscription_state:
          subscriptionState && subscriptionState.toLowerCase(),
        role,
        plan_code: planCode,
        campaign_id: campaignId,
        campaign_name: campaignName,
        device: getDeviceType(),
        sort_name: sortIdToTrackingValue[selectedSort],
        ...(teamId && {
          team_id: teamId,
        }),
      };
    }
  );

export const selectPageTrackingData = createSelector(
  (state) => state.userState,
  (state) => state.sortState.selectedSort,
  (state) => state.optimizelyState,
  (state) => state.socialEcommState.team.teamId,
  (userState, selectedSort, optimizelyState, teamId) => {
    const { userId, role, subscriptionState, planCode } = userState;

    return {
      user_id: userId || null,
      device: getDeviceType(),
      sort_name: sortIdToTrackingValue[selectedSort],
      //this data is not available on non-auth pages
      ...(role && { role }),
      ...(planCode && { plan_code: planCode }),
      ...(subscriptionState && {
        subscription_state: subscriptionState.toLowerCase(),
      }),
      ...(teamId && { team_id: teamId }),
    };
  }
);
